import { getGroups, getHiddenHash } from "./state";
import { getSelectedZones } from "../../helpers/column_names";
import { getSheet } from "../../sheets";
import {
	isInactiveSheet,
	getSheetGroups,
	setSheetGroups,
	withGroupSync,
	refreshGroupView,
} from "./helpers";

export function setOpenByRange(view, type, id, open, page) {
	const ranges = _resolveGroupRanges(view, type, id, page);
	if (!ranges) return;
	const inactiveSheet = isInactiveSheet(view, page);

	let sheet, groups;
	if (inactiveSheet) {
		sheet = getSheet(view, page);
		if (!sheet) return;
		groups = webix.copy(getSheetGroups(sheet, type));
	} else {
		groups = getGroups(view, type);
	}

	const seenIds = new Set();
	const targets = [];
	for (const [start, end] of ranges) {
		for (const i of _findGroupIndices(groups, start, end)) {
			if (!seenIds.has(groups[i].id) && groups[i].open !== open) {
				seenIds.add(groups[i].id);
				targets.push(i);
			}
		}
	}
	if (!targets.length) return;

	const relatedGroups = targets.flatMap(i =>
		_getRelatedGroups(groups, groups[i], open)
	);
	const prevStates = _setOpenToGroups(relatedGroups, open);

	const evStart = targets.reduce(
		(m, i) => Math.min(m, groups[i].start),
		Infinity
	);
	const evEnd = targets.reduce(
		(m, i) => Math.max(m, groups[i].end),
		-Infinity
	);
	const action = open ? "open-group" : "close-group";

	if (inactiveSheet) {
		setSheetGroups(sheet, type, groups);
		view.callEvent("onAction", [
			action,
			{
				value: {
					type,
					start: evStart,
					end: evEnd,
					states: prevStates,
					page,
				},
				newValue: {
					type,
					start: evStart,
					end: evEnd,
					states: relatedGroups.map(g => ({ id: g.id, open })),
					page,
				},
			},
		]);
		return;
	}

	_applyOpenChange(
		view,
		type,
		targets.map(i => groups[i]),
		relatedGroups,
		prevStates,
		open
	);
}

export function toggleGroup(view, type, index) {
	const groups = getGroups(view, type);
	if (index < 0 || index >= groups.length) return;

	const group = groups[index];
	const newOpen = !group.open;

	// when collapsing: also collapse nested sub-groups sharing the same end position
	// when expanding: also re-open ancestor groups that fully contain this range
	const relatedGroups = _getRelatedGroups(groups, group, newOpen);
	const prevStates = _setOpenToGroups(relatedGroups, newOpen);

	_applyOpenChange(view, type, [group], relatedGroups, prevStates, newOpen);
}

function _applyOpenChange(
	view,
	type,
	actionGroups,
	relatedGroups,
	prevStates,
	open
) {
	const grid = view.$$("cells");
	const evStart = actionGroups.reduce(
		(m, g) => Math.min(m, g.start),
		Infinity
	);
	const evEnd = actionGroups.reduce((m, g) => Math.max(m, g.end), -Infinity);
	const action = open ? "open-group" : "close-group";
	const hash = getHiddenHash(view, type);
	const prevHidden = webix.copy(hash);
	const prevSel = grid.getSelectArea();

	view.$handleSelection = () => false;

	withGroupSync(view, () => {
		for (const g of actionGroups) {
			if (open) _expandRange(view, type, g.start, g.end, g.level);
			else _setHiddenState(view, type, g.start, g.end, true);
		}
	});

	view.$handleSelection = null;

	// V: basic selection preservation
	// this should be done at hide/show level, while also preserving available row/column selection [WS-3596]
	if (prevSel) {
		const pos = type === "row" ? prevSel.start.row : prevSel.start.column;
		if (pos >= evStart && pos <= evEnd + 1) {
			const newRow = type === "row" ? evEnd + 1 : prevSel.start.row;
			const newCol = type === "column" ? evEnd + 1 : prevSel.start.column;
			const targetExists =
				type === "column"
					? grid.getColumnConfig(newCol)
					: grid.getItem(newRow);
			if (targetExists) grid.select(newRow, newCol);
		}
	}

	view.callEvent("onAction", [
		action,
		{
			value: {
				type,
				start: evStart,
				end: evEnd,
				states: prevStates,
				hiddenState: prevHidden,
			},
			newValue: {
				type,
				start: evStart,
				end: evEnd,
				states: relatedGroups.map(g => ({ id: g.id, open })),
				hiddenState: webix.copy(hash),
			},
		},
	]);

	refreshGroupView(view, type);
}

function _setHiddenState(view, type, start, end, hidden) {
	if (type === "row") view.hideRow([start, end], hidden);
	else view.hideColumn([start, end], hidden);
}

function _expandRange(view, type, start, end, level) {
	const groups = getGroups(view, type);

	// collect collapsed groups that block the expand, excluding outer ancestors
	const blocking = [];
	for (let i = 0; i < groups.length; i++) {
		const g = groups[i];
		if (!g.open) {
			// skip ancestor groups — outer groups that fully contain the expanding
			// range should not block a partial uncollapse of a sub-group
			if (
				level !== undefined &&
				g.level < level &&
				g.start <= start &&
				g.end >= end
			)
				continue;
			blocking.push(g);
		}
	}

	if (!blocking.length) {
		_setHiddenState(view, type, start, end, false);
		return;
	}

	// merge blocking intervals (sort by start, sweep to union overlaps)
	blocking.sort((a, b) => a.start - b.start);
	const merged = [{ start: blocking[0].start, end: blocking[0].end }];
	for (let i = 1; i < blocking.length; i++) {
		const last = merged[merged.length - 1];
		if (blocking[i].start <= last.end + 1) {
			if (blocking[i].end > last.end) last.end = blocking[i].end;
		} else {
			merged.push({ start: blocking[i].start, end: blocking[i].end });
		}
	}

	// emit show calls for the gaps between blocking intervals
	let pos = start;
	for (let i = 0; i < merged.length; i++) {
		const bStart = merged[i].start;
		const bEnd = merged[i].end;
		if (pos < bStart) {
			_setHiddenState(view, type, pos, Math.min(bStart - 1, end), false);
		}
		pos = bEnd + 1;
		if (pos > end) break;
	}
	if (pos <= end) {
		_setHiddenState(view, type, pos, end, false);
	}
}

export function restoreHiddenState(view, type, targetState, start, end) {
	const hash = getHiddenHash(view, type);

	// batch contiguous positions that need the same change into one API call
	let runStart = -1;
	let runHide = false;
	for (let i = start; i <= end + 1; i++) {
		if (i <= end) {
			const shouldBeHidden = !!targetState[i];
			if (shouldBeHidden !== !!hash[i]) {
				if (runStart === -1) {
					runStart = i;
					runHide = shouldBeHidden;
				} else if (shouldBeHidden !== runHide) {
					_setHiddenState(view, type, runStart, i - 1, runHide);
					runStart = i;
					runHide = shouldBeHidden;
				}
				continue;
			}
		}
		if (runStart !== -1) {
			_setHiddenState(view, type, runStart, i - 1, runHide);
			runStart = -1;
		}
	}
}

export function initHiddenStateFromGroups(view, type) {
	const groups = getGroups(view, type);
	withGroupSync(view, () => {
		for (let i = 0; i < groups.length; i++) {
			const g = groups[i];
			if (!g.open) _setHiddenState(view, type, g.start, g.end, true);
		}
	});
}

export function syncAndRefreshGroups(view, type) {
	_syncGroupsFromHiddenState(view, type);
	refreshGroupView(view, type);
}

export function syncOnHideShow(view, type, page) {
	if (isInactiveSheet(view, page)) {
		const sheet = getSheet(view, page);
		if (sheet) _syncGroupsInSheet(sheet, type);
	} else {
		if (!view._groupSyncing) {
			syncAndRefreshGroups(view, type);
		}
	}
}

function _syncGroupsFromHiddenState(view, type) {
	_syncGroupOpenState(getGroups(view, type), _makeHiddenChecker(view, type));
}

function _syncGroupsInSheet(sheet, type) {
	const t = sheet.content.table;
	if (!t || !t.groups || !t.groups[type]) return;
	const hiddenArr = (t.hidden && t.hidden[type]) || [];
	const hiddenSet = new Set();
	for (let i = 0; i < hiddenArr.length; i++)
		hiddenSet.add(String(hiddenArr[i]));
	const isHidden = pos => hiddenSet.has(String(pos));
	_syncGroupOpenState(t.groups[type], isHidden);
}

function _syncGroupOpenState(groups, isHidden) {
	for (let i = 0; i < groups.length; i++) {
		const g = groups[i];
		if (g.open && _allHidden(g.start, g.end, isHidden)) g.open = false;
		else if (!g.open && !_allHidden(g.start, g.end, isHidden))
			g.open = true;
	}
}

function _makeHiddenChecker(view, type) {
	if (type === "row") {
		// for rows: a row is effectively hidden if absent from data.order,
		// which covers both explicitly hidden rows and filter-excluded rows
		const order = new Set(view.$$("cells").data.order);
		return pos => !order.has(pos);
	} else {
		const hash = view._hidden_cols_hash;
		return pos => !!hash[pos];
	}
}

function _allHidden(start, end, isHidden) {
	for (let i = start; i <= end; i++) if (!isHidden(i)) return false;
	return true;
}

function _findGroupIndices(groups, start, end) {
	// priority 1: selection fully contains groups -> all at the lowest (outermost) level
	// g.end + 1 accounts for the icon position (visible group = start..end+1)
	let bestLevel = Infinity;
	for (let i = 0; i < groups.length; i++) {
		const g = groups[i];
		if (g.start >= start && g.end + 1 <= end && g.level < bestLevel)
			bestLevel = g.level;
	}
	if (bestLevel !== Infinity) {
		const result = [];
		for (let i = 0; i < groups.length; i++)
			if (
				groups[i].start >= start &&
				groups[i].end + 1 <= end &&
				groups[i].level === bestLevel
			)
				result.push(i);
		return result;
	}

	// priority 2: selection is (partially) inside a group -> single innermost
	let best = -1;
	bestLevel = -1;
	for (let i = 0; i < groups.length; i++) {
		const g = groups[i];
		if (g.start <= end && g.end + 1 >= start && g.level > bestLevel) {
			bestLevel = g.level;
			best = i;
		}
	}
	return best !== -1 ? [best] : [];
}

function _getRelatedGroups(groups, target, open) {
	if (open) {
		// expanding: include exact-range matches and collapsed ancestors
		return groups.filter(
			g =>
				(g.start === target.start && g.end === target.end) ||
				(!g.open &&
					g.level < target.level &&
					g.start <= target.start &&
					g.end >= target.end)
		);
	} else {
		// collapsing: include sub-groups sharing the same end position
		return groups.filter(
			g => g.end === target.end && g.start >= target.start
		);
	}
}

function _setOpenToGroups(relatedGroups, open) {
	const prev = relatedGroups.map(g => ({ id: g.id, open: g.open }));
	relatedGroups.forEach(g => {
		g.open = open;
	});
	return prev;
}

function _resolveGroupRanges(view, type, indexes, page) {
	if (!indexes) {
		if (isInactiveSheet(view, page)) return null;
		const zones = getSelectedZones(view, type);
		if (!zones || !zones.length) return null;
		return zones.map(z => [z.start[type], z.end[type]]);
	}
	if (webix.isArray(indexes))
		return [
			[
				Math.min(indexes[0], indexes[1]),
				Math.max(indexes[0], indexes[1]),
			],
		];
	return [[indexes, indexes]];
}
